import { useState } from 'react';
import { Shield, Smartphone, AlertTriangle, CheckCircle, Key, Download, Copy, Trash2 } from 'lucide-react';
import { use2FA } from '../hooks/use2FA';
import { TwoFactorSetupModal } from './TwoFactorSetupModal';

export function TwoFactorSettings() {
  const { 
    is2FAEnabled, 
    loading, 
    disable2FA, 
    regenerateBackupCodes 
  } = use2FA();
  
  const [showSetupModal, setShowSetupModal] = useState(false);
  const [showDisableConfirm, setShowDisableConfirm] = useState(false);
  const [disableCode, setDisableCode] = useState('');
  const [backupCodes, setBackupCodes] = useState<string[] | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const handleDisable2FA = async () => {
    if (!disableCode.trim()) {
      setError('Please enter a verification code');
      return;
    }

    setActionLoading(true);
    setError(null);

    try {
      const result = await disable2FA(disableCode);
      if (result.success) {
        setSuccess('2FA has been disabled successfully');
        setShowDisableConfirm(false);
        setDisableCode('');
      } else {
        setError(result.error || 'Failed to disable 2FA');
      }
    } catch (err) {
      setError('Failed to disable 2FA');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRegenerateBackupCodes = async () => {
    setActionLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const result = await regenerateBackupCodes();
      if (result.success && result.backupCodes) {
        setBackupCodes(result.backupCodes);
        setSuccess('New backup codes generated successfully');
      } else {
        setError(result.error || 'Failed to generate backup codes');
      }
    } catch (err) {
      setError('Failed to generate backup codes');
    } finally {
      setActionLoading(false);
    }
  };

  const downloadBackupCodes = (codes: string[]) => {
    const content = `Payinthi DMS 2FA Backup Codes\n\nGenerated: ${new Date().toLocaleString()}\n\nBackup Codes:\n${codes.join('\n')}\n\nIMPORTANT:\n- Store these codes in a safe place\n- Each code can only be used once\n- Use these codes if you lose access to your authenticator app`;
    
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'payinthi-dms-backup-codes.txt';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  if (loading) {
    return (
      <div className="bg-white dark:bg-slate-800 rounded-2xl p-6 border border-slate-200 dark:border-slate-700 shadow-lg">
        <div className="animate-pulse">
          <div className="h-6 bg-slate-200 dark:bg-slate-700 rounded mb-4"></div>
          <div className="h-20 bg-slate-200 dark:bg-slate-700 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="bg-white dark:bg-slate-800 rounded-2xl p-6 border border-slate-200 dark:border-slate-700 shadow-lg">
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg">
            <Shield className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="text-xl font-bold text-slate-900 dark:text-white">Two-Factor Authentication</h3>
            <p className="text-slate-600 dark:text-slate-400 text-sm">Add an extra layer of security to your account</p>
          </div>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/50 rounded-lg">
            <div className="flex items-center space-x-2">
              <AlertTriangle className="w-4 h-4 text-red-600 dark:text-red-400" />
              <p className="text-red-700 dark:text-red-400 text-sm">{error}</p>
            </div>
          </div>
        )}

        {success && (
          <div className="mb-4 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800/50 rounded-lg">
            <div className="flex items-center space-x-2">
              <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400" />
              <p className="text-green-700 dark:text-green-400 text-sm">{success}</p>
            </div>
          </div>
        )}

        {!is2FAEnabled ? (
          <div className="text-center py-8">
            <Smartphone className="w-16 h-16 text-slate-400 mx-auto mb-4" />
            <h4 className="text-lg font-medium text-slate-900 dark:text-white mb-2">
              2FA is not enabled
            </h4>
            <p className="text-slate-600 dark:text-slate-400 mb-6">
              Protect your account with two-factor authentication using an authenticator app.
            </p>
            <button
              onClick={() => setShowSetupModal(true)}
              className="inline-flex items-center space-x-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-3 rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 shadow-lg hover:shadow-xl"
            >
              <Shield className="w-4 h-4" />
              <span>Enable 2FA</span>
            </button>
          </div>
        ) : (
          <div className="space-y-6">
            <div className="flex items-center justify-between p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800/50 rounded-lg">
              <div className="flex items-center space-x-3">
                <CheckCircle className="w-6 h-6 text-green-600 dark:text-green-400" />
                <div>
                  <p className="font-medium text-green-800 dark:text-green-300">2FA is enabled</p>
                  <p className="text-sm text-green-600 dark:text-green-400">Your account is protected</p>
                </div>
              </div>
              <button
                onClick={() => setShowDisableConfirm(true)}
                className="flex items-center space-x-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
              >
                <Trash2 className="w-4 h-4" />
                <span>Disable</span>
              </button>
            </div>

            <div className="border-t border-slate-200 dark:border-slate-700 pt-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h4 className="font-medium text-slate-900 dark:text-white">Backup Codes</h4>
                  <p className="text-sm text-slate-600 dark:text-slate-400">
                    Generate new backup codes for account recovery
                  </p>
                </div>
                <button
                  onClick={handleRegenerateBackupCodes}
                  disabled={actionLoading}
                  className="flex items-center space-x-2 px-4 py-2 bg-slate-600 hover:bg-slate-700 text-white rounded-lg transition-colors disabled:opacity-50"
                >
                  <Key className="w-4 h-4" />
                  <span>{actionLoading ? 'Generating...' : 'Generate New'}</span>
                </button>
              </div>

              {backupCodes && (
                <div className="bg-slate-50 dark:bg-slate-700/50 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h5 className="font-medium text-slate-700 dark:text-slate-300">New Backup Codes</h5>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => copyToClipboard(backupCodes.join('\n'))}
                        className="flex items-center space-x-1 text-sm text-blue-600 hover:text-blue-700 transition-colors"
                      >
                        <Copy className="w-3 h-3" />
                        <span>Copy</span>
                      </button>
                      <button
                        onClick={() => downloadBackupCodes(backupCodes)}
                        className="flex items-center space-x-1 text-sm text-green-600 hover:text-green-700 transition-colors"
                      >
                        <Download className="w-3 h-3" />
                        <span>Download</span>
                      </button>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {backupCodes.map((code, index) => (
                      <div key={index} className="bg-white dark:bg-slate-600 px-3 py-2 rounded border">
                        <code className="text-sm font-mono">{code}</code>
                      </div>
                    ))}
                  </div>
                  <div className="mt-3 p-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/50 rounded">
                    <p className="text-xs text-amber-700 dark:text-amber-400">
                      Save these codes in a secure location. They replace your previous backup codes.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Disable 2FA Confirmation */}
        {showDisableConfirm && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-md flex items-center justify-center p-4 z-[9999]">
            <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl w-full max-w-md overflow-hidden border border-slate-200 dark:border-slate-700">
              <div className="p-6">
                <div className="text-center mb-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-red-500 to-red-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <AlertTriangle className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-2">
                    Disable Two-Factor Authentication
                  </h3>
                  <p className="text-slate-600 dark:text-slate-400 text-sm">
                    This will make your account less secure. Enter your authentication code to confirm.
                  </p>
                </div>

                <div className="mb-6">
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    Verification Code
                  </label>
                  <input
                    type="text"
                    value={disableCode}
                    onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="123456"
                    className="w-full px-4 py-3 border border-slate-200 dark:border-slate-600 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent transition-colors bg-white dark:bg-slate-700 text-slate-900 dark:text-white text-center text-lg font-mono"
                    maxLength={6}
                  />
                </div>

                <div className="flex space-x-3">
                  <button
                    onClick={() => {
                      setShowDisableConfirm(false);
                      setDisableCode('');
                      setError(null);
                    }}
                    disabled={actionLoading}
                    className="flex-1 px-4 py-3 border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 rounded-xl hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleDisable2FA}
                    disabled={actionLoading || disableCode.length !== 6}
                    className="flex-1 bg-gradient-to-r from-red-600 to-red-700 text-white px-4 py-3 rounded-xl hover:from-red-700 hover:to-red-800 transition-all duration-200 shadow-lg hover:shadow-xl disabled:opacity-50"
                  >
                    {actionLoading ? 'Disabling...' : 'Disable 2FA'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <TwoFactorSetupModal
        isOpen={showSetupModal}
        onClose={() => setShowSetupModal(false)}
        onComplete={() => {
          setSuccess('2FA has been enabled successfully');
          setTimeout(() => setSuccess(null), 5000);
        }}
      />
    </>
  );
}